import { isCloudPlatform } from "./runWhere";

/**
 * Which UI build is THIS page running, and does it match the one the server
 * serves? (2026-09-10.) A rebuild replaces every hashed chunk on disk, so an
 * open tab keeps working only until its next lazy import; `/api/health`
 * carries the served entry chunk (`ui_entry`) and the page compares it with
 * the entry script it loaded from. No build tooling, no extra field: the
 * hash in the entry filename is the build id both sides already have.
 */
const ENTRY_RE = /assets\/(index-[A-Za-z0-9_-]+\.js)/;
let cached: string | null = null;

export function runningUiEntry(): string {
	if (cached !== null) return cached;
	let found = "";
	if (typeof document !== "undefined") {
		for (const script of Array.from(document.scripts)) {
			const m = ENTRY_RE.exec(script.src || "");
			if (m) {
				found = m[1] ?? "";
				break;
			}
		}
	}
	cached = found;
	return found;
}

/** Tests only — the entry is read once per page load otherwise. */
export function _resetRunningUiEntryForTests(): void {
	cached = null;
}

/** How long the updated banner stays before the page loads the served build. */
export const UI_RELOAD_DELAY_MS = 800;

/** Load the build the server is serving. The open tab keeps the previous one until this runs. */
export function reloadPage(): void {
	window.location.reload();
}

/**
 * True when this page loaded a different entry than the server now serves.
 * An unknown entry on either side is not a change.
 * On the cloud platform the page comes from the site. The machine reports
 * its own bundle in /api/health. Those hashes differ on purpose, and a
 * reload still loads the site, so treating that as "updated" refreshes forever.
 */
export function uiBuildChanged(servedEntry: unknown, hostname?: string): boolean {
	const host = hostname ?? (typeof window === "undefined" ? "" : window.location.hostname);
	if (isCloudPlatform(host)) return false;
	const served = typeof servedEntry === "string" ? servedEntry : "";
	const running = runningUiEntry();
	return !!served && !!running && served !== running;
}
